<template>
  <!-- Filter Overlay Container -->
  <div
    class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
    @click.self="cancelFilter"
  >
    <div class="bg-pitch-black p-6 rounded-lg shadow-lg w-full max-w-md relative">
      <!-- Title -->
      <h2 class="text-2xl font-bold text-total-white mb-6">{{ title }}</h2>

      <!-- Dynamic Filters -->
      <div v-for="filter in filterState" :key="filter.key" class="mb-6">
        <!-- Multi-Select Filter -->
        <div v-if="filter.type === 'multi-select'" class="relative">
          <label class="text-total-white mb-2 block text-left">{{ filter.label }}</label>
          <div
            :class="[
              'flex relative items-center rounded-md border border-dark-grey',
              filter.dropdownOpen ? 'bg-dark-grey py-3' : 'bg-eerie-black py-2',
            ]"
          >
            <!-- Placeholder or Input -->
            <div
              v-if="!filter.dropdownOpen"
              @click="toggleDropdown(filter.key)"
              class="w-full flex justify-between items-center px-3 py-1 cursor-pointer"
            >
              <span class="text-total-white" v-if="filter.selected.length === 0">
                Select options
              </span>
              <span class="text-sm font-semibold text-total-white" v-else>
                {{ filter.selected.length }} Selected
              </span>
              <i class="fas fa-chevron-down text-total-white"></i>
            </div>

            <!-- Search Input -->
            <div v-else class="w-full flex items-center px-3 py-1">
              <input
                v-model="filter.searchQuery"
                type="text"
                placeholder="Search..."
                class="w-full bg-transparent border-none text-total-white outline-none"
              />
              <button @click="toggleDropdown(filter.key)" class="text-ash-grey ml-2">
                <i class="fas fa-times"></i>
              </button>
            </div>

            <!-- Dropdown List -->
            <div
              v-if="filter.dropdownOpen"
              class="absolute top-full mt-1 w-full left-0 bg-eerie-black border border-dark-grey rounded-md z-10"
            >
              <!-- No Options Found -->
              <div
                v-if="filteredOptions(filter).length === 0"
                class="p-4 text-center text-gray-500"
              >
                No options available.
              </div>
              <!-- Options List -->
              <ul>
                <li
                  v-for="option in filteredOptions(filter)"
                  :key="option.id"
                  @click="selectOption(filter, option)"
                  class="p-3 hover:bg-dark-grey cursor-pointer flex justify-between"
                >
                  <span>{{ option.label }}</span>
                  <i
                    v-if="filter.selected.some((item) => item.id === option.id)"
                    class="fas fa-check text-prime-golden"
                  ></i>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Selected Tags -->
        <div v-if="filter.type === 'multi-select'" class="flex flex-wrap gap-2 mt-4">
          <div
            v-for="selected in filter.selected"
            :key="selected.id"
            class="flex items-center bg-light-grey-translucent-500 text-white py-1 px-2 rounded-lg"
          >
            <span>{{ selected.label }}</span>
            <button
              @click="removeOption(filter, selected)"
              class="ml-2 text-xs text-ash-grey"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <!-- Single-Select Filter -->
        <div v-else-if="filter.type === 'select'" class="relative mb-8">
          <label class="text-total-white mb-2 block text-left">{{ filter.label }}</label>
          <div
            class="border border-dark-grey rounded-md bg-eerie-black px-4 py-3 flex items-center justify-between cursor-pointer"
            @click="toggleDropdown(filter.key)"
          >
            <span>{{ filter.selected[0]?.label || "Select an option" }}</span>
            <i
              :class="filter.dropdownOpen ? 'fa-chevron-up' : 'fa-chevron-down'"
              class="fas"
            ></i>
          </div>

          <!-- Dropdown List -->
          <div
            v-if="filter.dropdownOpen"
            class="absolute top-full mt-1 left-0 w-full bg-pitch-black border border-dark-grey z-10 rounded-md"
          >
            <ul>
              <li
                v-for="option in filter.options"
                :key="option.value"
                @click="selectSingleOption(filter, option)"
                class="p-2 hover:bg-dark-grey cursor-pointer px-4 py-3 text-left"
              >
                {{ option.label }}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="mt-4 flex justify-end">
        <button
          @click="clearFilters"
          class="px-4 py-2 bg-dark-grey text-total-white rounded-md hover:bg-dark-grey"
        >
          Clear Filters
        </button>
        <button
          @click="applyFilters"
          class="px-4 py-2 ml-2 bg-prime-golden text-black rounded-md hover:bg-golden-600"
        >
          Apply Filters
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { customFilterAndSort } from "@/misc/util";
import { reactive, toRefs } from "vue";

// Props and Emit
const props = defineProps({
  title: String,
  filters: Array, // Contains filters and state (multi-select, select, dropdownOpen, searchQuery, selected)
});

const emit = defineEmits(["apply", "cancel", "clear", "filter-change"]);

// Reactive state to handle dynamic filters
const filterState = reactive(props.filters);

// Toggle dropdown for a filter (only one dropdown open at a time)
const toggleDropdown = (key) => {
  filterState.forEach((filter) => {
    filter.dropdownOpen = filter.key === key ? !filter.dropdownOpen : false;
  });
};

// Filter options for search (multi-select)
const filteredOptions = (filter) => {
  const search = filter.searchQuery?.toLowerCase() || "";

  // Convert filter.options into the shape customFilterAndSort expects
  // i.e. each option must have a .label to match on.
  // (If you already store each option as { label, value, id }, you can skip this step.)
  // const mappedOptions = filter.options.map((o) => {
  //   return {
  //     label: o.label, // or something like `${o.label}`
  //     value: o.id, // or o.id, etc.
  //     id: o.id, // preserve your existing key
  //   };
  // });
  // Then call customFilterAndSort
  const sorted = customFilterAndSort(filter.options, search);

  return sorted;
};
const cancelFilter = () => {
  emit("cancel");
};

const applyFilters = () => {
  emit("apply-filters", filterState);
  console.log(filterState);
};
// Handle multi-select option selection
const selectOption = (filter, option) => {
  const isSelected = filter.selected.some((item) => item.id === option.id);
  if (isSelected) {
    filter.selected = filter.selected.filter((item) => item.id !== option.id);
  } else {
    filter.selected.push(option);
  }
  // Emit filter change to parent
  emit("filter-change", filter.key, filter.selected);
  toggleDropdown();
};

const removeOption = (filter, option) => {
  filter.selected = filter.selected.filter((item) => item.id !== option.id);
  // Emit filter change to parent
  emit("filter-change", filter.key, filter.selected);
};

const clearFilters = () => {
  filterState.forEach((filter) => {
    filter.selected = []; // Reset selected options
    filter.searchQuery = ""; // Reset search query
  });
  emit("clear"); // Emit clear event to parent
};

// Handle single-select dropdown
const selectSingleOption = (filter, option) => {
  filter.selected = [option];
  filter.dropdownOpen = false; // Close dropdown after selection
  // Emit filter change to parent
  emit("filter-change", filter.key, [option]);
};
</script>

<style scoped>
input[type="checkbox"] {
  width: 1rem;
  height: 1rem;
  accent-color: #d4af37; /* Prime golden color */
}
</style>
